import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, Chip,
  IconButton, Dialog, DialogActions, DialogContent, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Edit, Delete, DragIndicator } from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';

const ItemTypes = { TAG: 'instat-tag' };

// Same palette as the existing Tag.js so users get a familiar picker.
const colorCodes = [
  '#ffcc80', '#90caf9', '#a5d6a7', '#f48fb1',
  '#ffab91', '#ce93d8', '#81c784', '#64b5f6',
  '#ba68c8', '#4fc3f7', '#e57373', '#fff176',
];

const TagRow = ({ tag, index, moveTag, onEdit, onDelete, isSmallScreen, t }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.TAG,
    hover(item) {
      if (item.index === index) return;
      moveTag(item.index, index);
      item.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TAG,
    item: { id: tag.id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <TableRow ref={ref} hover sx={{ opacity: isDragging ? 0.6 : 1, cursor: 'move' }}>
      <TableCell sx={{ pl: 1, width: 40 }}>
        <DragIndicator fontSize="small" />
      </TableCell>
      <TableCell>
        <Chip
          label={tag.libelle}
          sx={{ backgroundColor: tag.code_couleur, color: '#fff' }}
        />
      </TableCell>
      <TableCell>
        <Box
          sx={{
            backgroundColor: tag.code_couleur,
            color: '#fff',
            padding: '4px 12px',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          {tag.code_couleur}
        </Box>
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={() => onEdit(tag)} sx={{ color: '#ff9800' }}>
          <Edit />
        </IconButton>
        <IconButton onClick={() => onDelete(tag)} sx={{ color: 'red' }}>
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const InstatTags = () => {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('');
  const [tags, setTags] = useState([]);
  const [editTag, setEditTag] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const sortTags = useCallback((arr) => {
    return [...arr].sort((a, b) => {
      if (a.ordre == null && b.ordre == null) {
        return a.libelle.localeCompare(b.libelle, 'es', { sensitivity: 'base' });
      }
      if (a.ordre == null) return 1;
      if (b.ordre == null) return -1;
      return a.ordre - b.ordre;
    });
  }, []);

  const fetchTags = async () => {
    try {
      const { data } = await api.get('/api/instat-tags');
      setTags(sortTags(data));
    } catch (err) {
      console.error('Erreur fetch instat tags:', err);
    }
  };

  useEffect(() => { fetchTags(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleAddTag = async () => {
    if (!tagName || !tagColor) return;
    try {
      const { data } = await api.post('/api/instat-tags', {
        libelle: tagName,
        code_couleur: tagColor,
      });
      setTags(sortTags([...tags, data]));
      setTagName('');
      setTagColor('');
    } catch (err) {
      if (err.response?.status === 409) {
        alert(t('instatTags.duplicateError'));
      } else {
        console.error('Erreur add instat tag:', err);
      }
    }
  };

  const handleEditTag = (tag) => {
    setEditTag(tag);
    setTagName(tag.libelle);
    setTagColor(tag.code_couleur);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/api/instat-tags/${editTag.id}`, {
        libelle: tagName,
        code_couleur: tagColor,
      });
      const updated = tags.map((tg) =>
        tg.id === editTag.id ? { ...tg, libelle: tagName, code_couleur: tagColor } : tg,
      );
      setTags(sortTags(updated));
      setTagName('');
      setTagColor('');
      setEditTag(null);
    } catch (err) {
      if (err.response?.status === 409) {
        alert(t('instatTags.duplicateError'));
      } else {
        console.error('Erreur update instat tag:', err);
      }
    }
  };

  const handleDeleteTag = async (tag) => {
    try {
      await api.delete(`/api/instat-tags/${tag.id}`);
      setTags(sortTags(tags.filter((tg) => tg.id !== tag.id)));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Erreur delete instat tag:', err);
    }
  };

  const moveTag = (fromIndex, toIndex) => {
    setTags((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, removed);
      return updated.map((t, idx) => ({ ...t, ordre: idx + 1 }));
    });
  };

  useEffect(() => {
    if (tags.length === 0) return;
    const persist = async () => {
      try {
        await api.put('/api/instat-tags/reorder', {
          orders: tags.map(({ id, ordre }) => ({ id, ordre })),
        });
      } catch (err) {
        console.error('Erreur reorder instat tags:', err);
        fetchTags();
      }
    };
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>{t('instatTags.title')}</Typography>

      <Grid container spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('instatTags.tagName')}
            variant="outlined"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Select
            value={tagColor}
            onChange={(e) => setTagColor(e.target.value)}
            displayEmpty
            variant="outlined"
            fullWidth
            renderValue={(selected) => {
              if (!selected) return <em>{t('instatTags.selectColor')}</em>;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 20, height: 20, backgroundColor: selected,
                    borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)',
                  }} />
                  {selected}
                </Box>
              );
            }}
          >
            <MenuItem value="" disabled>{t('instatTags.selectColor')}</MenuItem>
            {colorCodes.map((code) => (
              <MenuItem key={code} value={code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 20, height: 20, backgroundColor: code,
                    borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)',
                  }} />
                  {code}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            onClick={editTag ? handleSaveEdit : handleAddTag}
            sx={{ backgroundColor: '#ff9800', color: '#fff', height: '56px', width: '180px' }}
          >
            {editTag ? t('instatTags.saveChanges') : t('instatTags.addTag')}
          </Button>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>{t('instatTags.orderTags')}</Typography>

      <DndProvider backend={HTML5Backend}>
        <TableContainer component={Paper}>
          <Table size={isSmallScreen ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>{t('instatTags.label')}</TableCell>
                <TableCell>{t('instatTags.color')}</TableCell>
                <TableCell align="right">{t('instatTags.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((tag, index) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  index={index}
                  moveTag={moveTag}
                  onEdit={handleEditTag}
                  onDelete={setConfirmDelete}
                  isSmallScreen={isSmallScreen}
                  t={t}
                />
              ))}
              {tags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                    {t('instatTags.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DndProvider>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <Typography>{t('instatTags.confirmDelete', { name: confirmDelete?.libelle })}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('instatTags.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: 'grey' }}>{t('instatTags.cancel')}</Button>
          <Button onClick={() => handleDeleteTag(confirmDelete)} sx={{ color: 'red' }}>{t('instatTags.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstatTags;
